import { useState, useCallback, useMemo } from 'react';
import { Schema } from 'hooks/projects';
import { SchemaWithTypes } from '../useSchemaData';
import {
  ConsistencySchemaFilterView,
  ConsistencyCollectionView,
} from '../types';
import {
  GQLOperations,
  useUpdateConsistencyCollectionMutation,
} from 'types/dt-graphql-apollo-v2';
import { useAccessControl } from 'hooks/useAccessControl';

export type ReportData = Record<string, string[]>;

type SchemaEntry = {
  filter: ConsistencySchemaFilterView;
  currentSchema: Schema | undefined;
  allSchemas: Schema[];
};

const isReportData = (value: unknown): value is ReportData =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(
    (v) => Array.isArray(v) && v.every((item) => typeof item === 'string')
  );

const safeJsonParse = (input?: string | null): unknown => {
  if (input == null) return null;
  try {
    return JSON.parse(input);
  } catch {
    return null;
  }
};

export const parseReportTemplate = (
  reportTemplate?: string | null
): ReportData => {
  const parsed = safeJsonParse(reportTemplate);
  return isReportData(parsed) ? parsed : {};
};

function buildSchemaEntries(
  schemaFilters: ConsistencySchemaFilterView[],
  schemaData: SchemaWithTypes[]
): SchemaEntry[] {
  const seen = new Set<string>();
  const entries: SchemaEntry[] = [];

  for (const filter of schemaFilters) {
    if (seen.has(filter.schemaType)) continue;
    seen.add(filter.schemaType);

    const schemaWithTypes = schemaData.find(
      (s) => s.schemaCollection.externalId === filter.schemaCollectionId
    );
    const allSchemas = schemaWithTypes?.types ?? [];
    const currentSchema = allSchemas.find((s) => s.name === filter.schemaType);

    entries.push({ filter, currentSchema, allSchemas });
  }

  return entries;
}

const stringifySorted = (obj: ReportData) =>
  JSON.stringify(obj, Object.keys(obj).sort());

export const useRunConsistencyAttributesSelection = ({
  schemaFilters,
  schemaData,
  reportTemplate,
  collection,
}: {
  schemaFilters: ConsistencySchemaFilterView[];
  schemaData: SchemaWithTypes[];
  reportTemplate?: string | null;
  collection?: ConsistencyCollectionView;
}) => {
  const [selectedSchemaId, setSelectedSchemaId] = useState('');
  const [reportData, setReportData] = useState<ReportData>(
    parseReportTemplate(reportTemplate)
  );

  const schemaEntries = buildSchemaEntries(schemaFilters, schemaData);
  const hasAttributes = Object.values(reportData).some((v) => v.length > 0);

  const resetReport = () => setReportData({});

  const handleSchemaAttributesChange = (
    schemaType: string,
    attributes: string[]
  ) => {
    setReportData((current) => ({ ...current, [schemaType]: attributes }));
  };

  const canEditCollection = useAccessControl()([
    'Update_ConsistencyCollection',
  ]);

  const [updateReportTemplate, updateReportTemplateResult] =
    useUpdateConsistencyCollectionMutation();

  const saveReportTemplate = useCallback(async () => {
    if (!collection?.externalId) return;

    await updateReportTemplate({
      variables: {
        id: collection.externalId,
        reportTemplate: stringifySorted(reportData),
      },
      refetchQueries: [GQLOperations.Query.GetConsistencyCollections],
    });
  }, [collection?.externalId, reportData, updateReportTemplate]);

  const templateHasChanges = useMemo(
    () => (collection?.reportTemplate ?? '{}') !== stringifySorted(reportData),
    [collection?.reportTemplate, reportData]
  );

  const {
    data: saveResultData,
    error: saveError,
    loading: saveLoading,
  } = updateReportTemplateResult;

  const shouldShowSaveSection = canEditCollection;

  const saveResultFeedback = useMemo(() => {
    if (templateHasChanges) return null;
    if (saveResultData) return 'success';
    if (saveError) return 'error';
    return null;
  }, [saveResultData, saveError, templateHasChanges]);

  return {
    schemaEntries,
    reportData,
    hasAttributes,
    selectedSchemaId,
    setSelectedSchemaId,
    resetReport,
    handleSchemaAttributesChange,
    canEditCollection,
    saveReportTemplate,
    templateHasChanges,
    saveResultData,
    saveError,
    saveLoading,
    shouldShowSaveSection,
    saveResultFeedback,
  };
};
